import * as React from 'react';
import * as moment from 'moment';
import styled from 'styled-components';
import { debounce, uniqBy } from 'lodash';
import { Route, RouteComponentProps } from 'react-router-dom';
import theme from 'theme';
import t from 'utils/translations';
import { AutoComplete, Input as AntInput, Divider } from 'antd';
import { FontAwesomeIcon } from '@fortawesome/react-fontawesome';
import { AppContext } from 'components/context';
import {
    CompanySearchResponse,
    CompanySearchInfo
} from 'components/context/types';

const Search = styled(AutoComplete)`
    && {
        display: block;
        overflow: hidden;
        width: 100%;
    }
`;

const Input = styled(AntInput)`
        &&& {
          border-bottom: 1px solid ${theme.colors.border_light_gray};
          background-color: white;
          height: ${theme.componentMeasures.searchBarHeight};
          border-radius: 0;
        }
}
        &.ant-input-affix-wrapper .ant-input:not(:first-child) {
            border-color: transparent;
            border-radius: 0;
            padding-left: 40px;
    
            &:hover,
            &:focus {
                border-left-color: ${theme.colors.main};
                box-shadow: none;
                outline: none;
        }
`;

const SearchResult = styled.div`
    padding: 3px 10px;
    display: flex;
`;

const Icon = styled.div`
    font-size: 20px;
    color: ${theme.colors.gray_lighter};
    display: flex;
    align-items: center;
    margin: 0 15px 0 0;
    min-width: 20px;
`;

const Company = styled.p`
    color: ${theme.colors.main};
    margin: 0;
`;

const State = styled.p<{ active: boolean }>`
    color: ${p =>
        p.active ? theme.colors.green_active : theme.colors.red_deactive};
    margin: 0;
`;

const AdditionalCompanyInfo = styled.div`
    display: flex;
    align-items: center;
    color: ${theme.colors.gray_lighter};

    p {
        margin: 0;
    }
`;

export default function CompanySearch() {
    const [searchResults, setSearchResults] = React.useState<
        CompanySearchInfo[]
    >([]);
    const [searchTerm, setSearchTerm] = React.useState('');

    React.useEffect(() => {
        getHistory();
    }, []);

    const context = React.useContext(AppContext);
    if (!context) return null;

    const onSearch = (word: string) => {
        setSearchTerm(word);
    };

    const onSelect = (value: string, routeProps: RouteComponentProps) => {
        const company = JSON.parse(value);
        setSearchTerm('');
        saveHistory(value);
        routeProps.history.push(`/yritys/${company.businessId}`);
    };

    const onChange = debounce(
        (word: string) => {
            if (word.length >= 3) {
                sendSearchRequest(word)
                    .then((response: Response) => response.json())
                    .then((data: CompanySearchResponse) =>
                        constructAndSetSearchResults(data, word)
                    )
                    .catch(() => setSearchResults([]));
            } else if (!word) {
                setSearchResults([]);
                getHistory();
            } else {
                setSearchResults([]);
            }
        },
        500,
        { maxWait: 1000, leading: true }
    );

    const constructAndSetSearchResults = (
        data: CompanySearchResponse,
        word: string
    ) => {
        const companies = data.content;
        const history = getHistoryFromLocalStorage();

        if (history) {
            const historyNames = history.filter(item =>
                item.name.toLowerCase().includes(word.toLowerCase())
            );

            const currentResults = uniqBy(
                historyNames.concat(companies),
                'businessId'
            );

            setSearchResults(currentResults);
        } else {
            setSearchResults(companies);
        }
    };

    const getHistoryFromLocalStorage = (): CompanySearchInfo[] => {
        const pastSearchString = window.localStorage.getItem(
            'analyzerSearchHistory'
        );

        return pastSearchString ? JSON.parse(pastSearchString) : null;
    };

    const getHistory = () => {
        const companies = getHistoryFromLocalStorage();

        if (companies) {
            const freshCompanies = companies
                .filter(company => {
                    const isExpired = company.expires
                        ? moment().isBefore(company.expires)
                        : false;

                    return isExpired;
                })
                .reverse();

            window.localStorage.setItem(
                'analyzerSearchHistory',
                JSON.stringify(freshCompanies)
            );
            setSearchResults(freshCompanies);
        }
    };

    const saveHistory = (companyString: string) => {
        const company = JSON.parse(companyString);
        company.old = true;
        company.expires = moment().add(1, 'd');

        const pastSearchString = window.localStorage.getItem(
            'analyzerSearchHistory'
        );

        if (!pastSearchString) {
            const companyList = [company];
            const historyString = JSON.stringify(companyList);

            window.localStorage.setItem('analyzerSearchHistory', historyString);
        } else {
            const pastSearchHistory: CompanySearchInfo[] = JSON.parse(
                pastSearchString
            );
            const alreadyInHistory = pastSearchHistory.some(
                item => item.businessId === company.businessId
            );

            if (alreadyInHistory) return;

            if (pastSearchHistory.length > 3) {
                pastSearchHistory.shift();
            }

            pastSearchHistory.push(company);

            const companies = JSON.stringify(pastSearchHistory);
            window.localStorage.setItem('analyzerSearchHistory', companies);
        }
    };

    const sendSearchRequest = (value: string): Promise<any> => {
        try {
            const searchObject = JSON.parse(value);
            return context.actions.searchCompanies(
                (searchObject.businessId && searchObject.businessId) ||
                    searchObject
            );
        } catch (e) {
            return context.actions.searchCompanies(value);
        }
    };

    const dataSource =
        (searchResults &&
            searchResults.map(result => {
                const isActive = result.state === 'Aktiivinen';
                const companyObject = JSON.stringify(result); // Autocomplete.Option doesn't accept objects so was the solution

                return (
                    <AutoComplete.Option
                        key={result.name}
                        title={result.name}
                        value={companyObject}
                    >
                        <SearchResult>
                            <Icon>
                                {result.old && (
                                    <FontAwesomeIcon
                                        icon={['far', 'history']}
                                    />
                                )}
                            </Icon>
                            <div>
                                <Company>{result.name}</Company>
                                <AdditionalCompanyInfo>
                                    <p>{result.businessId}</p>
                                    <Divider type="vertical" />
                                    <p>{result.city}</p>
                                    <Divider type="vertical" />
                                    <State active={isActive}>
                                        {result.state}
                                    </State>
                                </AdditionalCompanyInfo>
                            </div>
                        </SearchResult>
                    </AutoComplete.Option>
                );
            })) ||
        [];

    return (
        <Route
            render={(routeProps: RouteComponentProps) => (
                <Search
                    autoFocus={true}
                    backfill={true}
                    defaultActiveFirstOption={false}
                    dataSource={dataSource}
                    dropdownClassName="company-search-dropdown"
                    onChange={onChange}
                    onSearch={onSearch}
                    onSelect={value => onSelect(value as string, routeProps)}
                    value={searchTerm}
                >
                    <Input
                        placeholder={t('COMPANY_SEARCH_PLACEHOLDER')}
                        prefix={
                            <FontAwesomeIcon
                                icon={['far', 'search']}
                                size="lg"
                            />
                        }
                    />
                </Search>
            )}
        />
    );
}
